import React, { useEffect, useReducer, useState } from 'react';
import {
  Box,
  IconButton,
  Typography,
} from '@mui/material';
import { FormattedMessage } from 'react-intl';
import ModeEditIcon from '@mui/icons-material/ModeEdit';
import CancelIcon from '@mui/icons-material/Cancel';
import SaveIcon from '@mui/icons-material/Save';
import {
  DigitalCase, FaerbungReviewScore, PartialBy,
} from '../../../../redux/types';
import { useAppDispatch, useAppSelector } from '../../../../hooks/reduxHooks';
import { selectFaerbungReviewScoreByCaseId, selectSelectedFaerbungReviewMeta } from '../../../../redux/reducers/FaerbungReviewReducer';
import {
  addUpdateFaerbungReviewScore,
  fetchFaerbungReviewMetas,
  fetchFaerbungReviewScores,
  resetFaerbungReviewMetas,
} from '../../../../redux/actions/faerbungReviewAction';
import ApiClient from '../../../../loopback/apiClient';
import {
  fetchSurveySubmissionMeta,
  setDigitalSubmissionMeta,
  setLoading,
  showErrorNotification,
  showNotification,
} from '../../../../redux/actions';
import FaerbungReviewTable, { faerbungTableReducer } from './FaerbungReviewTable';

type Props = {
  caseList: DigitalCase[];
  surveyId: number;
  sectionId: number;
  enrollmentId: number;
};

const FaerbungReviewEditor: React.FC<Props> = ({
  surveyId,
  sectionId,
  caseList,
  enrollmentId,
}: Props) => {
  const dispatch = useAppDispatch();
  const faerbungReviewMeta = useAppSelector(
    (state) => selectSelectedFaerbungReviewMeta(state, sectionId),
  );
  const faerbungScoresByCaseId = useAppSelector(
    (state) => selectFaerbungReviewScoreByCaseId(state),
  );

  const [editMode, setEditMode] = useState<boolean>(false);
  const [state, localDispatch] = useReducer(faerbungTableReducer, faerbungScoresByCaseId);

  useEffect(() => {
    dispatch(fetchFaerbungReviewMetas(surveyId));
    dispatch(fetchFaerbungReviewScores(surveyId, enrollmentId));
    return () => {
      dispatch(resetFaerbungReviewMetas());
    };
  }, [dispatch, surveyId, enrollmentId]);

  useEffect(() => {
    localDispatch({ type: 'resetState', payload: faerbungScoresByCaseId });
  }, [faerbungScoresByCaseId]);

  const evaluateFaerbungReview = () => {
    dispatch(setLoading(true));
    ApiClient.evaluate(enrollmentId)
      .then(() => ApiClient.getDigitalSubmissionMetaByEnrollment(enrollmentId))
      .then((dsms) => dispatch(setDigitalSubmissionMeta(dsms)))
      .then(() => dispatch(fetchSurveySubmissionMeta(enrollmentId)))
      .catch((e) => dispatch(showErrorNotification(e.message)))
      .finally(() => {
        dispatch(showNotification('Faerbung Review evaluation complete'));
        dispatch(setLoading(false));
      });
  };

  const closeEditHandler = () => {
    setEditMode(false);
    localDispatch({ type: 'resetState', payload: faerbungScoresByCaseId });
  };

  const saveScoreHandler = () => {
    const updatedRecords = Object.values(state).filter((frs) => frs.updated);
    const fReviewScores: PartialBy<FaerbungReviewScore, 'id'>[] = updatedRecords
      .map((frs) => {
        const { updated, ...rest } = frs;
        return rest;
      });
    if (fReviewScores.length) {
      dispatch(addUpdateFaerbungReviewScore(fReviewScores));
      evaluateFaerbungReview();
    }
    closeEditHandler();
  };

  if (!faerbungReviewMeta) {
    return null;
  }
  return (
    <Box>
      <Box display="flex">
        <Typography flexGrow={1} sx={{ my: 2 }} variant="h5">
          <FormattedMessage id="review.faerbungReviewTitle" />
        </Typography>
        <Box display="flex" alignItems="center" gap={1}>
          {
            editMode && (
              <>
                <IconButton color="primary" onClick={saveScoreHandler}>
                  <SaveIcon />
                </IconButton>
                <IconButton color="secondary" onClick={closeEditHandler}>
                  <CancelIcon />
                </IconButton>
              </>
            )
          }
          {
            !editMode && (
              <IconButton onClick={() => setEditMode(true)}>
                <ModeEditIcon />
              </IconButton>
            )
          }
        </Box>
      </Box>
      <FaerbungReviewTable
        caseList={caseList}
        faerbungReviewMeta={faerbungReviewMeta}
        faerbungScoresByCaseId={state}
        enrollmentId={enrollmentId}
        dispatch={localDispatch}
        editMode={editMode}
      />
    </Box>
  );
};

export default FaerbungReviewEditor;
